import { PET_KITS } from "../../render/skins/kits";
import { STAND_IN_PET, petModelName, petStockLook } from "../../render/skins/kits/pets";
import { WornSkins } from "../../render/skins/wornSkins";
import { uiUrl } from "../portraits";

/**
 * A pet's portrait (docs/PETS.md; the art agents' `art/interface/pets/<model>_256.png`, 256² transparent): the pet's
 * stock model name or its coat's (`pet_lantern_owl__snow_256.png`). The pictures arrive pet by pet (Pets v3), so the
 * markup falls back on its own — a coat's picture that is missing gives way to the stock one, a missing stock one to
 * the painted CSS stand-in under it (pets.css `.petport`: the pup's face, or a paw on the pet's own colour) — and a
 * picture that failed once is remembered for the session (`missing`), so it is never asked for again: one request,
 * not one per redraw.
 */

/** Portrait URLs that failed to load this session. */
const missing = new Set<string>();
/** The global the inline handlers call (markup strings cannot close over module state). */
const HANDLER = "__hmPetArtMissing";

if (typeof window !== "undefined") {
  (window as unknown as Record<string, unknown>)[HANDLER] = (img: HTMLImageElement): void => {
    missing.add(img.getAttribute("src") ?? "");
    const next = img.dataset.fallback;
    if (next && !missing.has(next) && img.getAttribute("src") !== next) { img.dataset.fallback = ""; img.src = next; return; }
    img.remove();
  };
}

/** The portrait file of pet `petId` in `skin` (null: the stock coat). */
export function petPortraitUrl(petId: string, skin: string | null = null): string {
  const kit = skin ? PET_KITS[skin] : undefined;
  const model = kit && kit.pet === petId ? kit.model : petModelName(petId);
  return uiUrl(`pets/${model}_256.png`);
}

/** The best picture not known to be missing (the coat's, else the stock one), or null when neither is there. */
export function petPortraitSrc(petId: string, skin: string | null = null): string | null {
  const own = petPortraitUrl(petId, skin), stock = petPortraitUrl(petId, null);
  return !missing.has(own) ? own : !missing.has(stock) ? stock : null;
}

/** An <img> of the portrait that falls back (coat → stock → gone) and remembers what failed; "" when nothing is left. */
export function petPortraitImg(petId: string, skin: string | null, attrs = ""): string {
  const src = petPortraitSrc(petId, skin), stock = petPortraitUrl(petId, null);
  if (src === null) return "";
  const fb = src !== stock && !missing.has(stock) ? ` data-fallback="${stock}"` : "";
  return `<img decoding="sync" src="${src}" alt=""${fb} ${attrs} onerror="${HANDLER}(this)">`;
}

/** Points `img` at the pet's portrait with the same fallbacks (a reused element: the boss bar's face). */
export function setPetPortrait(img: HTMLImageElement, petId: string, skin: string | null): void {
  const src = petPortraitSrc(petId, skin), stock = petPortraitUrl(petId, null), face = img.parentElement;
  face?.classList.remove("art");
  if (face && !face.querySelector(".pf-under")) face.insertAdjacentHTML("afterbegin", petStandInHtml(petId));
  img.onload = () => face?.classList.add("art");
  if (src === null) { img.removeAttribute("src"); img.classList.add("hidden"); return; }
  img.dataset.fallback = src !== stock ? stock : "";
  img.onerror = () => (window as unknown as Record<string, (i: HTMLImageElement) => void>)[HANDLER]?.(img);
  img.src = src;
  img.classList.remove("hidden");
}

/** The painted stand-in under the picture: the pup's face, or a paw on the pet's own colour. */
function standInHtml(petId: string): string {
  if (petId === STAND_IN_PET) return `<i class="pp-ears"></i><i class="pp-face"></i>`;
  const [base] = petStockLook(petId).swatch;
  return `<i class="pp-paw" style="background-color:#${base.toString(16).padStart(6, "0")}"></i>`;
}

/**
 * The stand-in alone, laid under a picture in a face of its own size (the wild note, the catch card, the escape, the
 * boss bar): pets.css hides it once the picture has loaded (`.art` on the face), so a pet with no portrait yet still
 * shows its paw.
 */
export function petStandInHtml(petId: string): string {
  return `<span class="petport pf-under${petId === STAND_IN_PET ? "" : " other"}" aria-hidden="true">${standInHtml(petId)}</span>`;
}

/** A face's inside: the stand-in, and the picture over it (the face gets `.art` once the picture is in). */
export function petFaceHtml(petId: string, skin: string | null): string {
  return petStandInHtml(petId) + petPortraitImg(petId, skin, `onload="this.parentElement.classList.add('art')"`);
}

/** The portrait at `px` CSS px, in the worn skin unless `skin` is given (null: the stock coat). */
export function petPortraitHtml(petId: string, px: number, skin?: string | null): string {
  const id = skin !== undefined ? skin : WornSkins.of("pet", petId);
  const img = petPortraitImg(petId, id, `onload="this.parentElement.classList.add('art')"`);
  return `<span class="petport${petId === STAND_IN_PET ? "" : " other"}" style="width:${px}px;height:${px}px" aria-hidden="true">${standInHtml(petId)}${img}</span>`;
}
